import React from 'react';
import { useTranslation } from 'react-i18next';
import { Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react';
import WhatsAppBooking from '../components/common/WhatsAppBooking';

const Contact = () => {
  const { t } = useTranslation();
  
  return (
    <>
      {/* Contact Hero */}
      <div className="bg-primary-600 pt-32 pb-20 px-4">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl font-bold text-white mb-6">{t('footer.contact.title')}</h1>
            <p className="text-xl text-white/80">{t('contact.subtitle')}</p>
          </div>
        </div>
      </div>
      
      {/* Contact Info */}
      <section className="section bg-white">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            <div className="card flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <Phone size={24} className="text-primary-600" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t('contact.phone.title')}</h3>
              <div className="text-gray-600 mb-4">
                <p className="mb-2">
                  <span className="font-medium">{t('contact.phone.clinic')}:</span><br />
                  968 59 50 16
                </p>
                <p>
                  <span className="font-medium">WhatsApp:</span><br />
                  +34 692248724
                </p>
              </div>
              <div className="space-y-2">
                <a 
                  href="tel:968595016" 
                  className="text-primary-600 font-medium hover:underline block"
                >
                  {t('contact.phone.call')}
                </a>
                <a 
                  href="https://wa.me/34692248724" 
                  className="text-primary-600 font-medium hover:underline block"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('contact.phone.whatsapp')}
                </a>
              </div>
            </div>
            
            <div className="card flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <Mail size={24} className="text-primary-600" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t('contact.email.title')}</h3>
              <p className="text-gray-600 mb-4">clinicdentalbahia@gmail.com</p>
              <a 
                href="mailto:clinicdentalbahia@gmail.com" 
                className="text-primary-600 font-medium hover:underline"
              >
                {t('contact.email.send')}
              </a>
            </div>
            
            <div className="card flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <Clock size={24} className="text-primary-600" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t('contact.hours.title')}</h3>
              <div className="text-gray-600">
                <p className="mb-2">
                  <span className="font-medium">{t('contact.hours.weekdays')}:</span><br />
                  9:00 - 14:00<br />
                  16:00 - 20:00
                </p>
                <p className="mb-2">
                  <span className="font-medium">{t('contact.hours.saturday')}:</span><br />
                  9:00 - 14:00
                </p>
                <p>
                  <span className="font-medium">{t('contact.hours.sunday')}:</span><br />
                  {t('contact.hours.closed')}
                </p>
              </div>
            </div>
          </div>
          
          {/* Map and Address */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
            <div className="rounded-lg overflow-hidden shadow-lg h-[400px]">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3153.7127335873366!2d-1.2567893!3d37.5619139!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd6419c5d7a12bd7%3A0x8cb3c42fe4149e95!2sAv.%20Dr.%20Meca%2C%2034%2C%2030860%20Puerto%20de%20Mazarr%C3%B3n%2C%20Murcia!5e0!3m2!1sen!2ses!4v1709985367736!5m2!1sen!2ses"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
            <div className="flex flex-col justify-center">
              <h3 className="text-2xl font-bold mb-4">{t('contact.location.title')}</h3>
              <p className="text-gray-600 text-lg mb-6">
                Av, Dr Meca, 34 Discovery Building, 1-1a<br />
                Puerto de Mazarrón, Murcia, 30860
              </p>
              <div className="space-y-4">
                <a 
                  href="https://maps.google.com/?q=Av+Dr+Meca+34+Puerto+de+Mazarron+Murcia+30860" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary inline-flex items-center"
                >
                  <MapPin size={20} className="mr-2" />
                  {t('contact.location.directions')}
                </a>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-6">{t('contact.cta.title')}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              {t('contact.cta.description')}
            </p>
            <a
              href="#appointment"
              className="btn bg-[#25D366] hover:bg-[#20BD5C] text-white inline-flex items-center gap-2"
            >
              <MessageCircle size={20} />
              {t('nav.book')}
            </a>
          </div>
        </div>
      </section>
      
      <WhatsAppBooking />
    </>
  );
};

export default Contact;